// Core Imports
import { api } from '@/core/lib/api'
import type { TResponse } from '@/core/types/axios'

// Module Imports
import {
  MAX_DOCUMENT_MB,
  questionGenerationEndpoints,
} from '@/modules/question-generation/question-generation.constants'
import type {
  TEditDraftRequest,
  TGeneratedQuestion,
  TGenerationBatch,
  TLlmOptions,
  TReviewQuestionRequest,
  TStartGenerationRequest,
} from '@/modules/question-generation/question-generation.types'

export async function getLlmOptions(): Promise<TLlmOptions> {
  const response = await api.get<TResponse<TLlmOptions>>(
    questionGenerationEndpoints.llmOptions
  )
  return response.data.data
}

export async function getBatchList(
  page: number,
  query: string,
  kind?: string
): Promise<TResponse<TGenerationBatch[]>> {
  const response = await api.get<TResponse<TGenerationBatch[]>>(
    questionGenerationEndpoints.listBatches(page, query, kind)
  )
  return response.data
}

export async function getBatchDetail(id: string): Promise<TGenerationBatch> {
  const response = await api.get<TResponse<TGenerationBatch>>(
    questionGenerationEndpoints.batchDetail(id)
  )
  return response.data.data
}

export async function getBatchQuestions(
  id: string
): Promise<TGeneratedQuestion[]> {
  const response = await api.get<TResponse<TGeneratedQuestion[]>>(
    questionGenerationEndpoints.batchQuestions(id)
  )
  return response.data.data
}

export async function startGeneration(
  payload: TStartGenerationRequest
): Promise<TGenerationBatch> {
  if (payload.document) {
    const isPdf =
      payload.document.type === 'application/pdf' ||
      payload.document.name.toLowerCase().endsWith('.pdf')
    const maxBytes = MAX_DOCUMENT_MB * 1024 * 1024

    if (!isPdf) {
      throw new Error('Yalnızca PDF belge yüklenebilir.')
    }
    if (payload.document.size > maxBytes) {
      throw new Error(`Belge en fazla ${MAX_DOCUMENT_MB}MB olabilir.`)
    }
  }

  // Multipart: criteria fields + optional PDF document (docling-parsed backend).
  const form = new FormData()
  form.append('name', payload.name)
  form.append('topic', payload.topic)
  form.append('count', String(payload.count))
  form.append('category_id', payload.category_id)
  if (payload.subcategory_id)
    form.append('subcategory_id', payload.subcategory_id)
  if (payload.question_pool_id)
    form.append('question_pool_id', payload.question_pool_id)
  form.append('difficulty', payload.difficulty)
  payload.match_modes.forEach((m) => form.append('match_modes', m))
  if (payload.language_tone) form.append('language_tone', payload.language_tone)
  if (payload.extra_instructions)
    form.append('extra_instructions', payload.extra_instructions)
  form.append('provider', payload.provider)
  form.append('model', payload.model)
  form.append('web_search', String(payload.web_search))
  // Otomatik AI doğrulama/onay politikası — her aksiyon bağımsız. Backend hazır
  // olunca tüketir; şimdilik tanımsız form alanları FastAPI'de yok sayılır (zararsız).
  form.append('auto_approve_enabled', String(payload.auto_approve_enabled))
  if (payload.auto_approve_enabled)
    form.append(
      'approve_confidence_threshold',
      String(payload.approve_confidence_threshold)
    )
  form.append('auto_revise_enabled', String(payload.auto_revise_enabled))
  if (payload.auto_revise_enabled)
    form.append('revise_max_attempts', String(payload.revise_max_attempts))
  form.append('auto_reject_enabled', String(payload.auto_reject_enabled))
  if (payload.auto_reject_enabled)
    form.append(
      'reject_confidence_threshold',
      String(payload.reject_confidence_threshold)
    )
  form.append('verify_with_web', String(payload.verify_with_web))
  // Doğrulama judge'ının gücü (model katmanı + oy sayısı). Backend `verification_rigor`
  // olarak tüketir; üretim/chat model seçiminden bağımsızdır (üretici≠denetçi).
  form.append('verification_rigor', payload.verification_rigor)
  // Taslak olarak kaydet → backend soru üretmez, DRAFT şablon olarak saklar.
  form.append('save_as_draft', String(payload.save_as_draft ?? false))
  if (payload.document) form.append('file', payload.document)

  // BE 201'de yeni batch'i (id/status) döndürür; kontratı dürüst tutmak için tipli
  // parse edilir (C-05). Çağıran şu an liste invalidate+navigate yapar; gövde ileride
  // (ör. detaya yönlendirme) kullanılabilir.
  const response = await api.post<TResponse<TGenerationBatch>>(
    questionGenerationEndpoints.createBatch,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  )
  return response.data.data
}

export async function editDraftBatch(
  payload: TEditDraftRequest
): Promise<TGenerationBatch> {
  const { id, ...criteria } = payload
  // Backend (UpdateDraftRequest) `extra="forbid"` → gövde yalnız kriter alanları
  // taşımalı (save_as_draft/document YOK). Alanlar açıkça kurulur.
  const response = await api.patch<TResponse<TGenerationBatch>>(
    questionGenerationEndpoints.updateDraft(id),
    {
      name: criteria.name,
      topic: criteria.topic,
      count: criteria.count,
      category_id: criteria.category_id,
      subcategory_id: criteria.subcategory_id || null,
      question_pool_id: criteria.question_pool_id || null,
      difficulty: criteria.difficulty,
      match_modes: criteria.match_modes,
      provider: criteria.provider,
      model: criteria.model,
      web_search: criteria.web_search,
      language_tone: criteria.language_tone,
      extra_instructions: criteria.extra_instructions,
      auto_approve_enabled: criteria.auto_approve_enabled,
      approve_confidence_threshold: criteria.approve_confidence_threshold,
      auto_revise_enabled: criteria.auto_revise_enabled,
      revise_max_attempts: criteria.revise_max_attempts,
      auto_reject_enabled: criteria.auto_reject_enabled,
      reject_confidence_threshold: criteria.reject_confidence_threshold,
      verify_with_web: criteria.verify_with_web,
      verification_rigor: criteria.verification_rigor,
    }
  )
  return response.data.data
}

export async function reviewQuestion(
  payload: TReviewQuestionRequest
): Promise<TGeneratedQuestion> {
  // BE güncel soruyu (statü + türetilmiş alanlar) döndürür; bununla tek öğe cache'te
  // yamalanır → tüm-liste refetch'i gerekmez (F-04).
  const response = await api.patch<TResponse<TGeneratedQuestion>>(
    questionGenerationEndpoints.reviewQuestion(payload.id),
    {
      action: payload.action,
      question: payload.question,
      choices: payload.choices,
      correct_choice: payload.correct_choice,
      acknowledge_duplicate: payload.acknowledge_duplicate,
    }
  )
  return response.data.data
}

export async function passiveBatch(id: string): Promise<void> {
  await api.post(questionGenerationEndpoints.passiveBatch(id))
}

export async function activateBatch(id: string): Promise<void> {
  await api.post(questionGenerationEndpoints.activateBatch(id))
}

export async function deleteBatch(id: string): Promise<void> {
  await api.delete(questionGenerationEndpoints.deleteBatch(id))
}

export async function deliverBatch(id: string): Promise<TGenerationBatch> {
  // Onaylanan soruları Bilgilig'e aktar; güncel batch (import durumu + sayaçlar) döner.
  const response = await api.post<TResponse<TGenerationBatch>>(
    questionGenerationEndpoints.deliverBatch(id)
  )
  return response.data.data
}
